import { CUSTOM_ELEMENTS_SCHEMA, Component, OnInit, computed, input, output, signal } from '@angular/core';
import { BeneficioOpcao, RegraImpedimento } from '../../../../core/models/parametros/beneficios.model';
import { ANOS_MAXIMO, MENSAGEM_IMPEDIMENTO_MAXIMO } from '../../../../core/utils/parametros/beneficios.defaults';
import { VivestModalAreaVisivelDirective } from '../../../../shared/directives/modal-vivest/vivest-modal-area-visivel.directive';

export type ImpedimentoEditado = Omit<RegraImpedimento, 'id' | 'idApi'>;

type CondicaoIdade = 'idadeMinima' | 'idadeMaxima';

const CONDICOES_IDADE: readonly { id: CondicaoIdade; rotulo: string; prefixo: string; padrao: number }[] = [
  { id: 'idadeMinima', rotulo: 'Idade mínima', prefixo: 'Mínimo de', padrao: 55 },
  { id: 'idadeMaxima', rotulo: 'Idade máxima', prefixo: 'Máximo de', padrao: 70 },
];

/** Quem atender a todas as condições marcadas fica impedido de solicitar o benefício. */
@Component({
  selector: 'app-impedimento-modal',
  standalone: true,
  imports: [VivestModalAreaVisivelDirective],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './impedimento-modal.component.html',
  styleUrl: './impedimento-modal.component.scss',
})
export class ImpedimentoModalComponent implements OnInit {
  readonly beneficio = input.required<string>();
  /** Outros benefícios do plano, que podem ser condição do impedimento. */
  readonly opcoes = input.required<readonly BeneficioOpcao[]>();
  /** Regra em edição; `null` cria uma regra nova. */
  readonly regra = input<RegraImpedimento | null>(null);

  readonly salvar = output<ImpedimentoEditado>();
  readonly fechar = output<void>();

  readonly condicoesIdade = CONDICOES_IDADE;
  readonly mensagemMaximo = MENSAGEM_IMPEDIMENTO_MAXIMO;

  readonly beneficiosMarcados = signal<ReadonlySet<number>>(new Set());
  /** Condição de idade marcada → valor digitado (texto). */
  readonly idades = signal<ReadonlyMap<CondicaoIdade, string>>(new Map());
  readonly mensagem = signal('');
  /** Os erros só aparecem depois de uma tentativa de salvar. */
  readonly tentouSalvar = signal(false);

  readonly titulo = computed(() => `${this.regra() ? 'Editar' : 'Adicionar'} regra de ${this.beneficio()}`);
  readonly semCondicao = computed(() => this.beneficiosMarcados().size === 0 && this.idades().size === 0);

  /** Erro do valor de cada idade marcada; sem erro, não entra no mapa. */
  readonly errosIdade = computed(() => {
    const erros = new Map<CondicaoIdade, string>();
    for (const [id, valor] of this.idades()) {
      const numero = Number(valor);
      if (!valor || numero > ANOS_MAXIMO) {
        erros.set(id, `Informe de 0 a ${ANOS_MAXIMO} anos.`);
      }
    }
    const minima = this.idades().get('idadeMinima');
    const maxima = this.idades().get('idadeMaxima');
    if (!erros.size && minima && maxima && Number(minima) > Number(maxima)) {
      erros.set('idadeMaxima', 'A idade máxima não pode ser menor que a mínima.');
    }
    return erros;
  });

  ngOnInit(): void {
    const regra = this.regra();
    if (regra) {
      this.beneficiosMarcados.set(new Set(regra.beneficios));
      const idades = new Map<CondicaoIdade, string>();
      for (const condicao of CONDICOES_IDADE) {
        const valor = regra[condicao.id];
        if (valor !== null) {
          idades.set(condicao.id, String(valor));
        }
      }
      this.idades.set(idades);
      this.mensagem.set(regra.mensagem);
    }
  }

  alternarBeneficio(id: number, marcado: boolean): void {
    const proximos = new Set(this.beneficiosMarcados());
    if (marcado) {
      proximos.add(id);
    } else {
      proximos.delete(id);
    }
    this.beneficiosMarcados.set(proximos);
  }

  idadeMarcada(id: CondicaoIdade): boolean {
    return this.idades().has(id);
  }

  valorIdade(id: CondicaoIdade): string {
    return this.idades().get(id) ?? '';
  }

  /** Marcar uma condição de idade já preenche o valor padrão. */
  alternarIdade(condicao: (typeof CONDICOES_IDADE)[number], marcada: boolean): void {
    const proximas = new Map(this.idades());
    if (marcada) {
      proximas.set(condicao.id, String(condicao.padrao));
    } else {
      proximas.delete(condicao.id);
    }
    this.idades.set(proximas);
  }

  digitarIdade(id: CondicaoIdade, campo: HTMLInputElement): void {
    const digitos = campo.value.replace(/\D/g, '');
    campo.value = digitos;
    this.idades.set(new Map(this.idades()).set(id, digitos));
  }

  confirmar(): void {
    this.tentouSalvar.set(true);
    if (this.semCondicao() || this.errosIdade().size) {
      return;
    }
    const idade = (id: CondicaoIdade) => (this.idades().has(id) ? Number(this.idades().get(id)) : null);
    this.salvar.emit({
      // Na ordem da lista, não na ordem em que foram marcados.
      beneficios: this.opcoes()
        .map((opcao) => opcao.id)
        .filter((id) => this.beneficiosMarcados().has(id)),
      idadeMinima: idade('idadeMinima'),
      idadeMaxima: idade('idadeMaxima'),
      mensagem: this.mensagem().trim(),
    });
  }
}
